import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Checkbox, Input, Message, Placeholder, Tooltip, Whisper } from 'rsuite';
import { Button, Modal, useLastPresent } from '@nsi-advisory-services/components';
import { useExpenseAttachmentContent, useExpenseAttachments } from '../../hooks/useQueries';
import { getCategoryLabel } from '../../utils/expenseCategories';
import { STAGE_AWAITING, getExpenseStage } from '../../utils/expenseWorkflow';
import './ApprovalReviewModal.css';

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const formatDate = (value) => {
  if (!value) return '';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    const match = DATE_ONLY_PATTERN.exec(trimmed);
    if (match) {
      const year = Number(match[1]);
      const monthIndex = Number(match[2]) - 1;
      const day = Number(match[3]);
      const localDate = new Date(year, monthIndex, day);
      return localDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: '2-digit' });
    }
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: '2-digit' });
};

const formatCurrency = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
};

const formatDateTime = (value) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatBytes = (value) => {
  const size = Number(value);
  if (!Number.isFinite(size) || size <= 0) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
};

const triggerBlobDownload = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName || 'attachment';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const triggerBlobPreview = (blob) => {
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener,noreferrer');
  setTimeout(() => URL.revokeObjectURL(url), 60 * 1000);
};

const formatTimeFromMinutes = (minutes) => {
  const m = Number(minutes);
  if (!Number.isFinite(m)) return '';
  const h24 = Math.floor(m / 60) % 24;
  const mins = m % 60;
  const ampm = h24 >= 12 ? 'PM' : 'AM';
  let h12 = h24 % 12;
  if (h12 === 0) h12 = 12;
  return mins === 0 ? `${h12} ${ampm}` : `${h12}:${String(mins).padStart(2, '0')} ${ampm}`;
};

const formatDisplayValue = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  return String(value);
};

// A date-only string is a calendar date; going through the Date constructor
// would pull it into the local timezone and shift the day.
const parseDateOnly = (value) => {
  if (!value) return null;
  if (typeof value === 'string') {
    const match = DATE_ONLY_PATTERN.exec(value.trim());
    if (match) return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

// Matches the icons TimeOffRequestCard uses, so a request looks the same in the
// employee's list and in the approver's modal.
const TIME_OFF_TYPES = {
  vacation: { icon: 'bi-airplane-fill', label: 'Vacation' },
  sick: { icon: 'bi-heart-pulse-fill', label: 'Sick' },
  reduction: { icon: 'bi-clock-fill', label: 'Reduction' },
};

const getRequestTypeMeta = (value) => {
  const key = String(value || '').trim().toLowerCase();
  return TIME_OFF_TYPES[key]
    || { icon: 'bi-calendar-event', label: key ? key[0].toUpperCase() + key.slice(1) : 'Time Off' };
};

const StatusPill = ({ status }) => {
  const normalized = String(status || '').toLowerCase();
  const toneClass = normalized === 'approved'
    ? 'approval-review-modal__status-pill--approved'
    : normalized === 'rejected'
      ? 'approval-review-modal__status-pill--rejected'
      : 'approval-review-modal__status-pill--pending';

  return (
    <span className={`approval-review-modal__status-pill ${toneClass}`}>
      <span className="approval-review-modal__status-dot" />
      <span>{formatDisplayValue(status)}</span>
    </span>
  );
};

const ExpenseField = ({ label, value, wide }) => (
  <div className={`approval-review-modal__field${wide ? ' approval-review-modal__field--wide' : ''}`}>
    <span className="approval-review-modal__field-label">{label}</span>
    <span className="approval-review-modal__field-value">{formatDisplayValue(value)}</span>
  </div>
);

const ExpenseChip = ({ icon, label, tone, active }) => (
  <span
    className={`approval-review-modal__chip ${active
      ? (tone ? `approval-review-modal__chip--${tone}` : '')
      : 'approval-review-modal__chip--off'}`}
  >
    <i className={`bi ${active ? icon : 'bi-dash-circle'}`} aria-hidden="true" />
    <span>{label}</span>
    <span className="approval-review-modal__chip-state">{active ? 'Yes' : 'No'}</span>
  </span>
);

const ExpenseNote = ({ label, value }) => (
  <div className="approval-review-modal__note">
    <div className="approval-review-modal__note-label">{label}</div>
    <div className="approval-review-modal__note-body">{value}</div>
  </div>
);

export default function ApprovalReviewModal({
  open,
  onClose,
  record: recordProp,
  recordType,
  onApprove,
  onReject,
  canDecide = true,
  onSaveComment,
  onPrintReceipt,
  onToggleQboReceipt,
  onMarkPaid,
  loading,
  commentLoading,
  qboReceiptLoading,
  markPaidLoading,
  approvalBlockedReason,
}) {
  const record = useLastPresent(recordProp); // keeps the dialog steady while it fades out
  const [reason, setReason] = useState('');
  // Approving an expense requires an explicit receipt attestation first.
  const [approveConfirmOpen, setApproveConfirmOpen] = useState(false);
  const [receiptsConfirmed, setReceiptsConfirmed] = useState(false);
  const [totalConfirmed, setTotalConfirmed] = useState(false);
  const expenseId = recordType === 'expense' ? record?.id : null;
  const attachmentsQuery = useExpenseAttachments(expenseId, {
    enabled: open && recordType === 'expense' && Boolean(expenseId),
  });
  const contentMutation = useExpenseAttachmentContent();
  // A second instance so prefetching for drag-out doesn't put the row's
  // Preview/Download buttons into their loading state.
  const dragPrefetchMutation = useExpenseAttachmentContent();
  // attachment id -> object URL held for the lifetime of this modal.
  const [dragUrls, setDragUrls] = useState({});
  const dragUrlsRef = useRef({});
  const [isPreparingDragCopies, setIsPreparingDragCopies] = useState(false);

  // Seed the box with any comment already saved on the record so it reads as
  // editing that comment rather than starting over. Deliberately keyed on the
  // record id, not on approval_reason: re-seeding on every background refetch
  // would clobber whatever is currently being typed.
  useEffect(() => {
    if (!open) return;
    setReason(record?.approval_reason || '');
    setApproveConfirmOpen(false);
    setReceiptsConfirmed(false);
    setTotalConfirmed(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, record?.id]);

  const canSubmit = reason.trim().length > 0;
  const approvalBlocked = Boolean(approvalBlockedReason);
  // Approve/reject/comment all write the same row, so let only one run at a
  // time: a decision landing mid-comment-save (or vice versa) makes the loser
  // fail its `status = 'pending'` guard and report a spurious "modified by
  // someone else" to the very person who caused it.
  const busy = Boolean(loading) || Boolean(commentLoading);
  const status = String(record?.status || '').toLowerCase();
  const isPending = status === 'pending';
  // Pending and this person can decide it: shows the reason box and the
  // approve / reject / comment buttons.
  const decidable = isPending && canDecide;

  // Attribution for a comment saved on a still-pending record. approved_by is
  // null until a decision is made, so this comes from its own columns.
  const savedCommentBy = useMemo(() => {
    if (!record?.commented_at) return '';
    const name = `${record.commenter_first_name || ''} ${record.commenter_last_name || ''}`.trim()
      || record.commenter_email
      || '';
    const at = formatDateTime(record.commented_at);
    if (name && at) return `Last saved by ${name} on ${at}`;
    if (at) return `Last saved on ${at}`;
    return name ? `Last saved by ${name}` : '';
  }, [record?.commented_at, record?.commenter_first_name, record?.commenter_last_name, record?.commenter_email]);
  const showReviewMetadata = status === 'approved' || status === 'rejected';
  const isApprovedPaid = recordType === 'expense' && status === 'approved' && Boolean(record?.paid_on);
  const qboUploaded = Boolean(record?.receipt_uploaded_to_qbo);
  // The same test the payroll board applies, so an expense that is settled
  // without a reimbursement — a company card purchase — is never offered a
  // payment date it doesn't owe. The caller decides who is allowed to see this
  // at all by handing over onMarkPaid, or not.
  const canMarkPaid = recordType === 'expense'
    && Boolean(onMarkPaid)
    && getExpenseStage(record) === STAGE_AWAITING;

  // Time off gets the same shape as an expense — a headline figure, the flags
  // that change a decision, then the facts — rather than the row-per-field list
  // it used to render. The two are read by the same person in the same sitting.
  const timeOffView = useMemo(() => {
    if (!record || recordType === 'expense') return null;

    const first = record.first_name || record.employee_first_name || '';
    const last = record.last_name || record.employee_last_name || '';
    const startTime = record.start_minutes == null ? '' : formatTimeFromMinutes(record.start_minutes);
    const endTime = record.end_minutes == null ? '' : formatTimeFromMinutes(record.end_minutes);
    const start = parseDateOnly(record.start_date);
    const end = parseDateOnly(record.end_date);
    // Inclusive: a request that starts and ends on the same day is one day off.
    const days = start && end
      ? Math.round((end.getTime() - start.getTime()) / 86400000) + 1
      : null;
    const approverName = `${record.approver_first_name || ''} ${record.approver_last_name || ''}`.trim()
      || record.approver_email
      || record.approved_by;

    return {
      employeeName: `${first} ${last}`.trim(),
      type: getRequestTypeMeta(record.request_type),
      hours: record.total_hours != null ? `${Number(record.total_hours)} hrs` : '—',
      days,
      start: `${formatDate(record.start_date)}${startTime ? ` @ ${startTime}` : ''}`,
      end: `${formatDate(record.end_date)}${endTime ? ` @ ${endTime}` : ''}`,
      multipleDays: Boolean(days && days > 1),
      // Either bound carrying a clock time means this isn't a whole day off.
      partialDay: record.start_minutes != null || record.end_minutes != null,
      onCalendar: Boolean(record.add_to_company_calendar),
      submitted: record.created_at ? formatDate(record.created_at) : '',
      notes: record.notes,
      approverName,
      reviewedAt: record.approved_at ? formatDate(record.approved_at) : '',
    };
  }, [record, recordType]);

  const expenseView = useMemo(() => {
    if (!record || recordType !== 'expense') return null;

    const first = record.first_name || record.employee_first_name || '';
    const last = record.last_name || record.employee_last_name || '';
    const employeeName = `${first} ${last}`.trim();
    const hasMultipleDates = Boolean(record.has_multiple_purchase_dates);
    const purchaseDate = hasMultipleDates && (record.purchase_start_date || record.purchase_end_date)
      ? `${formatDate(record.purchase_start_date)} – ${formatDate(record.purchase_end_date)}`
      : formatDate(record.purchase_date);
    const travelDates = record.travel && (record.travel_start_date || record.travel_end_date)
      ? `${formatDate(record.travel_start_date)} – ${formatDate(record.travel_end_date)}`
      : '';
    const approverName = `${record.approver_first_name || ''} ${record.approver_last_name || ''}`.trim()
      || record.approver_email
      || record.approved_by;

    return {
      employeeName,
      category: getCategoryLabel(record.category),
      amount: formatCurrency(record.amount),
      purchaseDate,
      vendor: record.vendor_name,
      travel: Boolean(record.travel),
      travelDates,
      companyCard: Boolean(record.purchased_by_me),
      customerReimbursable: Boolean(record.customer_reimbursable),
      customerName: record.customer_name,
      multipleDates: hasMultipleDates,
      reason: record.reason_for_purchase,
      notes: record.description,
      approverName,
      reviewedAt: record.approved_at ? formatDate(record.approved_at) : '',
      paidOn: record.paid_on ? formatDate(record.paid_on) : '',
      invoicedOn: record.invoiced_on ? formatDate(record.invoiced_on) : '',
      qboUploaded: Boolean(record.receipt_uploaded_to_qbo),
    };
  }, [record, recordType]);

  const title = isPending
    ? (recordType === 'expense' ? 'Review Expense' : 'Review Time Off Request')
    : (recordType === 'expense' ? 'Expense Approval Details' : 'Time Off Approval Details');

  const attachmentRows = useMemo(() => attachmentsQuery.data || [], [attachmentsQuery.data]);
  // Identity of the list rather than the array, so a background refetch that
  // returns the same files doesn't re-run the prefetch below.
  const attachmentIdsKey = useMemo(
    () => attachmentRows.map((row) => row.id).join('|'),
    [attachmentRows]
  );

  // Dragging a file out to the desktop rides on the DownloadURL data format,
  // which only Chromium honours. Everywhere else the drag is a no-op, so don't
  // advertise it — or pay for the prefetch it needs.
  const supportsDragOut = useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    const brands = navigator.userAgentData?.brands;
    if (Array.isArray(brands)) return brands.some((brand) => /Chromium/i.test(brand?.brand || ''));
    const ua = navigator.userAgent || '';
    // The iOS variants are WebKit underneath and don't support it.
    return /Chrome\/|Chromium\/|Edg\//.test(ua) && !/CriOS|EdgiOS|FxiOS/.test(ua);
  }, []);

  // Chrome fetches the dropped URL itself, without our Authorization header,
  // so the API URL can't be handed over directly. Pull each attachment down
  // while the reviewer reads the expense and hand over a blob: URL instead.
  useEffect(() => {
    if (!open || !supportsDragOut || recordType !== 'expense' || !expenseId) return;
    if (attachmentRows.length === 0) return;

    let cancelled = false;

    (async () => {
      setIsPreparingDragCopies(true);
      for (const row of attachmentRows) {
        if (cancelled) return;
        if (!row?.id || dragUrlsRef.current[row.id]) continue;
        try {
          const result = await dragPrefetchMutation.mutateAsync({
            expenseId,
            attachmentId: row.id,
            download: true,
          });
          if (cancelled) return;
          const url = URL.createObjectURL(result.blob);
          dragUrlsRef.current = { ...dragUrlsRef.current, [row.id]: url };
          setDragUrls(dragUrlsRef.current);
        } catch {
          // Leave this row undraggable; Preview/Download still work.
        }
      }
      if (!cancelled) setIsPreparingDragCopies(false);
    })();

    return () => {
      cancelled = true;
      Object.values(dragUrlsRef.current).forEach((url) => URL.revokeObjectURL(url));
      dragUrlsRef.current = {};
      setDragUrls({});
      setIsPreparingDragCopies(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, supportsDragOut, recordType, expenseId, attachmentIdsKey]);

  const handleAttachmentDragStart = (event, row) => {
    const url = dragUrls[row?.id];
    if (!url) {
      event.preventDefault();
      return;
    }

    // Format is "contentType:fileName:url" and it is split on the first two
    // colons, so a colon in the name would truncate it.
    const fileName = String(row.file_name || 'attachment').replace(/:/g, '_');
    const contentType = row.content_type || 'application/octet-stream';
    event.dataTransfer.setData('DownloadURL', `${contentType}:${fileName}:${url}`);
    event.dataTransfer.effectAllowed = 'copy';
  };

  const handlePreviewAttachment = async (row) => {
    if (!expenseId || !row?.id) return;
    try {
      const result = await contentMutation.mutateAsync({
        expenseId,
        attachmentId: row.id,
        download: false,
      });
      triggerBlobPreview(result.blob);
    } catch {
      return;
    }
  };

  // Expense approvals go through a receipt attestation dialog; time off is
  // approved straight from the footer button.
  const requiresApprovalConfirmation = recordType === 'expense';
  const approvalConfirmed = receiptsConfirmed && totalConfirmed;

  const handleApproveClick = () => {
    if (requiresApprovalConfirmation) {
      setReceiptsConfirmed(false);
      setTotalConfirmed(false);
      setApproveConfirmOpen(true);
      return;
    }
    onApprove?.(reason.trim());
  };

  const handleConfirmApprove = async () => {
    if (!approvalConfirmed) return;
    try {
      await onApprove?.(reason.trim());
    } finally {
      setApproveConfirmOpen(false);
    }
  };

  // Everything greying out Approve, ordered by how much the reviewer can do
  // about it. The button's disabled state is derived from this same list, so a
  // greyed-out button always has something to say for itself.
  const approveBlockers = useMemo(() => {
    const reasons = [];
    if (approvalBlockedReason) reasons.push(String(approvalBlockedReason));
    if (!canSubmit) reasons.push('Enter a comment or reason above.');
    if (busy) reasons.push('Another decision on this request is still saving.');
    if (!isPending) reasons.push('This request is no longer pending.');
    return reasons;
  }, [approvalBlockedReason, canSubmit, busy, isPending]);

  const approveDisabled = approveBlockers.length > 0;

  // Closing is the header's X, so the footer is decisions and record actions
  // only — and disappears when there are none of either.
  const hasFooterActions = Boolean(
    (recordType === 'expense' && onPrintReceipt)
    || (isApprovedPaid && onToggleQboReceipt)
    || canMarkPaid
    || decidable
  );

  const handleDownloadAttachment = async (row) => {
    if (!expenseId || !row?.id) return;
    try {
      const result = await contentMutation.mutateAsync({
        expenseId,
        attachmentId: row.id,
        download: true,
      });
      triggerBlobDownload(result.blob, row.file_name);
    } catch {
      return;
    }
  };

  return (
    <>
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      className="approval-review-modal"
    >
      <Modal.Header>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {expenseView ? (
          <div className="approval-review-modal__record">
            <div className="approval-review-modal__hero">
              <div className="approval-review-modal__hero-main">
                <div className="approval-review-modal__amount">{expenseView.amount}</div>
                <div className="approval-review-modal__hero-sub">
                  <span className="approval-review-modal__hero-name">{expenseView.employeeName || '-'}</span>
                  {expenseView.category ? (
                    <>
                      <span className="approval-review-modal__hero-dot" />
                      <span>Category: {expenseView.category}</span>
                    </>
                  ) : null}
                </div>
              </div>
              <StatusPill status={record.status} />
            </div>

            <div className="approval-review-modal__chips">
              <ExpenseChip icon="bi-airplane-fill" label="Travel" active={expenseView.travel} />
              <ExpenseChip icon="bi-credit-card-2-front-fill" label="Company card" tone="warn" active={expenseView.companyCard} />
              <ExpenseChip icon="bi-arrow-repeat" label="Customer reimbursable" tone="accent" active={expenseView.customerReimbursable} />
              <ExpenseChip icon="bi-calendar-range-fill" label="Multiple dates" active={expenseView.multipleDates} />
            </div>

            <div className="approval-review-modal__grid">
              <ExpenseField label="Purchase Date" value={expenseView.purchaseDate} />
              <ExpenseField label="Vendor" value={expenseView.vendor} />
              {expenseView.travel && expenseView.travelDates ? (
                <ExpenseField label="Travel Dates" value={expenseView.travelDates} />
              ) : null}
              {expenseView.customerReimbursable ? (
                <ExpenseField label="Customer" value={expenseView.customerName} />
              ) : null}
            </div>

            {expenseView.reason ? <ExpenseNote label="Reason for purchase" value={expenseView.reason} /> : null}
            {expenseView.notes ? <ExpenseNote label="Notes" value={expenseView.notes} /> : null}

            {(showReviewMetadata || expenseView.paidOn || expenseView.invoicedOn) ? (
              <div className="approval-review-modal__meta">
                {showReviewMetadata ? (
                  <>
                    <ExpenseField label="Reviewed By" value={expenseView.approverName} />
                    <ExpenseField label="Reviewed On" value={expenseView.reviewedAt} />
                  </>
                ) : null}
                {expenseView.paidOn ? (
                  <>
                    <ExpenseField label="Paid On" value={expenseView.paidOn} />
                    <ExpenseField label="Receipt in QBO" value={expenseView.qboUploaded ? 'Uploaded' : 'Not uploaded'} />
                  </>
                ) : null}
                {expenseView.invoicedOn ? (
                  <ExpenseField label="Invoiced On" value={expenseView.invoicedOn} />
                ) : null}
              </div>
            ) : null}
          </div>
        ) : timeOffView ? (
          <div className="approval-review-modal__record">
            <div className="approval-review-modal__hero">
              <div className="approval-review-modal__hero-main">
                <div className="approval-review-modal__amount">{timeOffView.hours}</div>
                <div className="approval-review-modal__hero-sub">
                  <span className="approval-review-modal__hero-name">{timeOffView.employeeName || '-'}</span>
                  <span className="approval-review-modal__hero-dot" />
                  <span>
                    <i className={`bi ${timeOffView.type.icon}`} aria-hidden="true" /> {timeOffView.type.label}
                  </span>
                </div>
              </div>
              <StatusPill status={record.status} />
            </div>

            <div className="approval-review-modal__chips">
              <ExpenseChip icon="bi-calendar-range-fill" label="Multiple days" active={timeOffView.multipleDays} />
              <ExpenseChip icon="bi-clock-fill" label="Partial day" tone="warn" active={timeOffView.partialDay} />
              <ExpenseChip icon="bi-calendar2-check-fill" label="On company calendar" tone="accent" active={timeOffView.onCalendar} />
            </div>

            <div className="approval-review-modal__grid">
              <ExpenseField label="Start" value={timeOffView.start} />
              <ExpenseField label="End" value={timeOffView.end} />
              <ExpenseField
                label="Days Off"
                value={timeOffView.days == null ? '' : `${timeOffView.days} day${timeOffView.days === 1 ? '' : 's'}`}
              />
              <ExpenseField label="Submitted" value={timeOffView.submitted} />
            </div>

            {timeOffView.notes ? <ExpenseNote label="Notes" value={timeOffView.notes} /> : null}

            {showReviewMetadata ? (
              <div className="approval-review-modal__meta">
                <ExpenseField label="Reviewed By" value={timeOffView.approverName} />
                <ExpenseField label="Reviewed On" value={timeOffView.reviewedAt} />
              </div>
            ) : null}
          </div>
        ) : null}

        {recordType === 'expense' ? (
          <div className="approval-review-modal__input-section">
            <div className="approval-review-modal__input-label">Attachments</div>

            {supportsDragOut && attachmentRows.length > 0 ? (
              <div className="text-muted" style={{ fontSize: 12, marginBottom: 8 }}>
                {isPreparingDragCopies
                  ? 'Preparing files for drag and drop…'
                  : 'Tip: drag a file straight to your desktop to save a copy.'}
              </div>
            ) : null}

            {attachmentsQuery.isLoading ? <Placeholder.Paragraph rows={3} active /> : null}

            {attachmentsQuery.error ? (
              <Message type="error" showIcon style={{ marginBottom: 12 }}>
                {String(attachmentsQuery.error?.message || attachmentsQuery.error)}
              </Message>
            ) : null}

            {contentMutation.error ? (
              <Message type="error" showIcon style={{ marginBottom: 12 }}>
                {String(contentMutation.error?.message || contentMutation.error)}
              </Message>
            ) : null}

            {!attachmentsQuery.isLoading ? (
              attachmentRows.length === 0 ? (
                <div className="text-muted">No attachments found for this expense.</div>
              ) : (
                <div className="approval-review-modal__attachments-list">
                  {attachmentRows.map((row) => {
                    const isDraggable = Boolean(dragUrls[row.id]);

                    return (
                    <div
                      key={row.id}
                      className="approval-review-modal__attachment-item"
                      draggable={isDraggable}
                      onDragStart={(event) => handleAttachmentDragStart(event, row)}
                      style={isDraggable ? { cursor: 'grab' } : undefined}
                      title={isDraggable ? 'Drag to your desktop to save a copy' : undefined}
                    >
                      <div>
                        <div className="approval-review-modal__attachment-file">
                          {isDraggable ? (
                            <i className="bi bi-grip-vertical me-1" aria-hidden="true" style={{ opacity: 0.5 }} />
                          ) : null}
                          {row.file_name}
                        </div>
                        <div className="approval-review-modal__attachment-meta text-muted">
                          {formatBytes(row.file_size_bytes)}
                          {row.uploader_name ? ` • Uploaded by ${row.uploader_name}` : ''}
                          {row.created_at ? ` • ${formatDateTime(row.created_at)}` : ''}
                        </div>
                      </div>
                      <div className="d-flex gap-2">
                        <Button
                          size="sm"
                          loading={contentMutation.isPending}
                          onClick={() => handlePreviewAttachment(row)}
                        >
                          Preview
                        </Button>
                        <Button
                          size="sm"
                          variant="primary"
                          loading={contentMutation.isPending}
                          onClick={() => handleDownloadAttachment(row)}
                        >
                          Download
                        </Button>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )
            ) : null}
          </div>
        ) : null}

        {decidable ? (
          <div className="approval-review-modal__input-section">
            {approvalBlocked ? (
              <Message type="warning" showIcon style={{ marginBottom: 12 }}>
                {approvalBlockedReason}
              </Message>
            ) : null}
            <div className="approval-review-modal__input-label">Comment / Reason (required)</div>
            <Input
              className="approval-review-modal__textarea"
              as="textarea"
              rows={4}
              placeholder="Enter a comment, or your decision reason..."
              value={reason}
              onChange={(value) => setReason(value)}
            />
            {savedCommentBy ? (
              <div className="text-muted small mt-1">{savedCommentBy}</div>
            ) : null}
          </div>
        ) : null}

        {!isPending && record?.approval_reason ? (
          <div className="approval-review-modal__input-section">
            <div className="approval-review-modal__input-label">Review Comment</div>
            <div className="approval-review-modal__read-only-comment">{record.approval_reason}</div>
          </div>
        ) : null}
      </Modal.Body>
      {/* Without a Close button the footer can end up holding nothing at all —
          a decided request with no receipt to print — and an empty bar with a
          border on it reads as something that failed to load. */}
      {hasFooterActions ? (
        <Modal.Footer
          left={(recordType === 'expense' && onPrintReceipt) || (isApprovedPaid && onToggleQboReceipt) ? (
            <>
              {recordType === 'expense' && onPrintReceipt ? (
                <Button icon="printer" onClick={() => onPrintReceipt(record)}>
                  Print as receipt
                </Button>
              ) : null}
              {isApprovedPaid && onToggleQboReceipt ? (
                <Button
                  variant={qboUploaded ? 'secondary' : 'accent'}
                  loading={!!qboReceiptLoading}
                  onClick={() => onToggleQboReceipt(record, !qboUploaded)}
                >
                  {qboUploaded ? 'Unmark QBO receipt' : 'Mark receipt uploaded to QBO'}
                </Button>
              ) : null}
            </>
          ) : null}
        >
          {canMarkPaid ? (
            <Button
              variant="primary"
              loading={!!markPaidLoading}
              onClick={() => onMarkPaid(record)}
            >
              Mark Paid
            </Button>
          ) : null}
          {decidable ? (
            <>
              {onSaveComment ? (
                <Button
                  disabled={!canSubmit || !isPending || busy}
                  loading={!!commentLoading}
                  onClick={() => onSaveComment(reason.trim())}
                >
                  Save Comment Without Approving
                </Button>
              ) : null}
              <Button
                variant="danger"
                disabled={!canSubmit || !isPending || approvalBlocked || busy}
                loading={!!loading}
                onClick={() => onReject?.(reason.trim())}
              >
                Reject
              </Button>
              {approveDisabled ? (
                <Whisper
                  placement="top"
                  trigger="hover"
                  speaker={(
                    <Tooltip>
                      <div style={{ textAlign: 'left' }}>
                        {approveBlockers.length === 1 ? approveBlockers[0] : (
                          <>
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>Approval is blocked by:</div>
                            <ul style={{ margin: 0, paddingLeft: 18 }}>
                              {approveBlockers.map((blocker) => (
                                <li key={blocker}>{blocker}</li>
                              ))}
                            </ul>
                          </>
                        )}
                      </div>
                    </Tooltip>
                  )}
                >
                  {/* A disabled button never fires the hover that opens a tooltip,
                      so the span is what the whisper listens on and the button's
                      own pointer events are turned off. */}
                  <span style={{ display: 'inline-block', cursor: 'not-allowed' }}>
                    <Button
                      variant="primary"
                      disabled
                      loading={!!loading}
                      style={{ pointerEvents: 'none' }}
                    >
                      Approve
                    </Button>
                  </span>
                </Whisper>
              ) : (
                <Button
                  variant="primary"
                  loading={!!loading}
                  onClick={handleApproveClick}
                >
                  Approve
                </Button>
              )}
            </>
          ) : null}
        </Modal.Footer>
      ) : null}
    </Modal>

    <Modal
      open={approveConfirmOpen}
      onClose={() => {
        if (busy) return;
        setApproveConfirmOpen(false);
      }}
      size="xs"
      backdrop="static"
    >
      <Modal.Header>
        <Modal.Title>Confirm Approval</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex flex-column gap-2">
          <Checkbox
            checked={receiptsConfirmed}
            onChange={(_value, checked) => setReceiptsConfirmed(checked)}
            disabled={busy}
          >
            I confirm that individual receipts are attached for every item
          </Checkbox>
          <Checkbox
            checked={totalConfirmed}
            onChange={(_value, checked) => setTotalConfirmed(checked)}
            disabled={busy}
          >
            I confirm that the total requested is exactly equal to the sum of all uploaded receipts
          </Checkbox>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={() => setApproveConfirmOpen(false)} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirmApprove}
          loading={!!loading}
          disabled={!approvalConfirmed || busy}
        >
          Approve
        </Button>
      </Modal.Footer>
    </Modal>
    </>
  );
}
